import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import SignatureCanvas from 'react-signature-canvas';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { ArrowLeft, LogIn, Hash, User, DoorOpen, CalendarIcon, Eraser, Send, AlertTriangle } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import { API, pageVariants, cleanEmployeeName } from './utils';
import { playWelcomeWithName, playVoiceMessage, stopAllAudio, speakMessage, getTimeBasedGreeting } from './voiceUtils';
import { TimeSelector } from './TimeSelector';
import { useGuestPortal } from './GuestPortalContext';
import { useRoomList } from './useRoomList';
import { logFailedCheckin } from './failedCheckinLog';

export function CheckInForm({ setView, setSuccessMessage, setNoticeAccepted, acceptedNoticeId, setAcceptedNoticeId }) {
  const { t, language } = useGuestPortal();
  const [employeeNumber, setEmployeeNumber] = useState("");
  const [employeeName, setEmployeeName] = useState("");
  const [roomNumber, setRoomNumber] = useState("");
  const [date, setDate] = useState(new Date());
  const getCurrentTime = () => {
    const now = new Date();
    return `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
  };
  const [time, setTime] = useState(getCurrentTime());
  const [telegramUsername, setTelegramUsername] = useState("");
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [employeeStatus, setEmployeeStatus] = useState(null);
  const [similarNumbers, setSimilarNumbers] = useState([]);
  const { rooms: availableRooms, roomsLoaded } = useRoomList();
  const [signatureReminderSpoken, setSignatureReminderSpoken] = useState(false);
  const [requestingAccess, setRequestingAccess] = useState(false);
  const [companyName, setCompanyName] = useState("");
  const [wrongAttempts, setWrongAttempts] = useState(0);
  const [hadTelegramSaved, setHadTelegramSaved] = useState(false);
  
  const [employeeMessage, setEmployeeMessage] = useState(null);
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [messageAcknowledged, setMessageAcknowledged] = useState(false);
  
  const sigRef = useRef(null);
  const roomInputRef = useRef(null);
  const signatureContainerRef = useRef(null);

  // Message check is handled by the unified verify-checkin endpoint below
  // No separate API call needed

  const handleAcknowledgeMessage = async () => {
    if (employeeMessage?.id) {
      try {
        await axios.post(`${API}/admin/employee-messages/${employeeMessage.id}/acknowledge`);
      } catch (error) {
        console.log("Error acknowledging message:", error);
      }
    }
    setShowMessageModal(false);
    setMessageAcknowledged(true);
    setEmployeeMessage(null);
  };

  const handleTelegramOptOut = async () => {
    if (!employeeNumber) return;
    try {
      await axios.post(`${API}/guests/telegram-opt-out`, { employee_number: employeeNumber });
      setTelegramUsername('');
      setHadTelegramSaved(false);
      toast.success(language === 'es' ? 'Has cancelado las notificaciones de Telegram' : 'You\'ve opted out of Telegram notifications');
    } catch (error) {
      toast.error(language === 'es' ? 'Error al cancelar' : 'Failed to opt out');
    }
  };

  useEffect(() => {
    const interval = setInterval(() => setTime(getCurrentTime()), 60000);
    return () => clearInterval(interval);
  }, []);

  // Safety net: if the user navigates away while the signature scroll-lock is
  // still engaged (mid-draw interruption, toast modal, etc.), make sure the
  // page isn't left stuck with overflow:hidden.
  useEffect(() => {
    return () => {
      if (typeof document !== 'undefined' && document.body.dataset._sigLock === '1') {
        document.body.style.overflow = document.body.dataset._prevOverflow || '';
        document.body.style.touchAction = document.body.dataset._prevTouchAction || '';
        document.documentElement.style.overflow = document.body.dataset._prevHtmlOverflow || '';
        delete document.body.dataset._sigLock;
        delete document.body.dataset._prevOverflow;
        delete document.body.dataset._prevTouchAction;
        delete document.body.dataset._prevHtmlOverflow;
      }
    };
  }, []);

  useEffect(() => {
    if (employeeNumber.length < 5) {
      setEmployeeStatus(null);
      setEmployeeName("");
      setSimilarNumbers([]);
      return;
    }

    const verifyEmployee = async (retryCount = 0) => {
      const MAX_RETRIES = 3;  // 4 total attempts — protects against transient Atlas slowness
      setVerifying(true);
      setSimilarNumbers([]);
      try {
        // Single unified endpoint — runs guest + employee + message lookups in parallel on backend
        const response = await axios.get(`${API}/verify-checkin/${employeeNumber}`, { timeout: 12000 });
        const data = response.data;
        
        if (data.found) {
          const cleanedName = cleanEmployeeName(data.name || '');
          setEmployeeName(cleanedName);
          setEmployeeStatus('found');
          if (data.telegram_username) {
            setTelegramUsername(data.telegram_username);
            setHadTelegramSaved(true);
          }
          if (data.message?.has_message) {
            setEmployeeMessage(data.message.message);
            setShowMessageModal(true);
          }
          if (cleanedName) {
            playWelcomeWithName(cleanedName, false);
          }
          setTimeout(() => roomInputRef.current?.focus(), 300);
        } else {
          setEmployeeName("");
          setEmployeeStatus('not_found');
          if (data.similar_numbers?.length > 0) {
            setSimilarNumbers(data.similar_numbers);
          }
          playVoiceMessage("yellow_card_instructions");
        }
      } catch (error) {
        // Silent auto-retry on transient errors (Atlas hiccups, network blips, timeouts).
        // This is a background lookup as the user types, so no toast needed.
        const status = error?.response?.status;
        const isTransient = error.code === 'ECONNABORTED'
          || error.message === 'Network Error'
          || !error.response
          || status === 502 || status === 503 || status === 504;
        if (isTransient && retryCount < MAX_RETRIES) {
          const delay = 500 * Math.pow(2, retryCount);  // 500 / 1000 / 2000 ms
          setTimeout(() => verifyEmployee(retryCount + 1), delay);
          return;
        }
        setEmployeeName("");
        setEmployeeStatus('not_found');
        playVoiceMessage("yellow_card_instructions");
      } finally {
        setVerifying(false);
      }
    };

    const timer = setTimeout(verifyEmployee, 800);
    return () => clearTimeout(timer);
  }, [employeeNumber]);

  const handleRequestAccess = async () => {
    if (!employeeName.trim()) {
      toast.error("Please enter your name");
      return;
    }
    setRequestingAccess(true);
    try {
      await axios.post(`${API}/request-employee-access`, {
        employee_number: employeeNumber,
        name: employeeName.trim()
      });
      toast.success("Access request sent! Admin will approve shortly.");
      setEmployeeStatus(null);
      setEmployeeNumber("");
      setEmployeeName("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send request. Please contact admin.");
    } finally {
      setRequestingAccess(false);
    }
  };

  const handleContinueAsNewEmployee = async () => {
    if (!employeeName.trim()) {
      toast.error("Please enter your name");
      return;
    }
    if (companyName.trim().toUpperCase() !== "CPKC") {
      const newAttempts = wrongAttempts + 1;
      setWrongAttempts(newAttempts);
      if (newAttempts >= 2) {
        toast.error("Invalid company name. Please call Help Phone from outside office.");
        playVoiceMessage("help_phone_message");
      } else {
        toast.error("Invalid company name. Please try again.");
      }
      return;
    }
    setRequestingAccess(true);
    try {
      await axios.post(`${API}/guests/register-pending`, {
        employee_number: employeeNumber,
        name: employeeName.trim()
      });
      setEmployeeStatus('found');
      setWrongAttempts(0);
      playWelcomeWithName(employeeName.trim(), true);
      toast.success("Welcome! Please continue with check-in.");
      setTimeout(() => roomInputRef.current?.focus(), 300);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to register. Please try again.");
    } finally {
      setRequestingAccess(false);
    }
  };

  const speakSignatureReminder = () => {
    if (!signatureReminderSpoken) {
      speakMessage("Please sign your full name legibly.");
      setSignatureReminderSpoken(true);
    }
  };

  const clearSignature = () => {
    sigRef.current?.clear();
    setSignatureReminderSpoken(false);
  };

  // Lock the page scroll while the guest is drawing their signature so the
  // canvas can't drift up/down under their finger. Uses overflow:hidden on the
  // root html/body which keeps the visible viewport exactly where it is — no
  // jump, no React tree disturbance (earlier position:fixed approach caused the
  // form to scroll-reset and looked like the page was navigating back).
  const lockBodyScroll = () => {
    if (typeof document === 'undefined') return;
    const html = document.documentElement;
    const body = document.body;
    if (body.dataset._sigLock === '1') return; // already locked
    body.dataset._sigLock = '1';
    body.dataset._prevOverflow = body.style.overflow || '';
    body.dataset._prevTouchAction = body.style.touchAction || '';
    body.dataset._prevHtmlOverflow = html.style.overflow || '';
    body.style.overflow = 'hidden';
    body.style.touchAction = 'none';
    html.style.overflow = 'hidden';
  };
  const unlockBodyScroll = () => {
    if (typeof document === 'undefined') return;
    const html = document.documentElement;
    const body = document.body;
    if (body.dataset._sigLock !== '1') return;
    body.style.overflow = body.dataset._prevOverflow || '';
    body.style.touchAction = body.dataset._prevTouchAction || '';
    html.style.overflow = body.dataset._prevHtmlOverflow || '';
    delete body.dataset._sigLock;
    delete body.dataset._prevOverflow;
    delete body.dataset._prevTouchAction;
    delete body.dataset._prevHtmlOverflow;
  };

  const validateSignature = () => {
    if (!sigRef.current || sigRef.current.isEmpty()) {
      return { valid: false, reason: "Please provide your signature" };
    }
    const canvas = sigRef.current.getCanvas();
    const ctx = canvas.getContext('2d');
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const pixels = imageData.data;
    let inkPixels = 0;
    for (let i = 0; i < pixels.length; i += 4) {
      if (pixels[i] < 250 || pixels[i + 1] < 250 || pixels[i + 2] < 250) inkPixels++;
    }
    const totalPixels = canvas.width * canvas.height;
    const inkPercentage = (inkPixels / totalPixels) * 100;
    const signatureData = sigRef.current.toData();
    let totalPoints = 0;
    let strokeCount = signatureData.length;
    signatureData.forEach(stroke => { totalPoints += stroke.length; });
    
    if (inkPercentage < 0.15) {
      return { valid: false, reason: "Signature is too simple. Please sign your full name." };
    }
    if (totalPoints < 30) {
      return { valid: false, reason: "Signature is too short. Please sign your full name legibly." };
    }
    if (strokeCount <= 2 && totalPoints < 80) {
      return { valid: false, reason: "Simple marks like 'X' are not accepted. Please sign your full name." };
    }
    if (strokeCount === 1 && totalPoints < 100) {
      const points = signatureData[0];
      if (points && points.length > 0) {
        let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
        points.forEach(point => {
          minX = Math.min(minX, point.x); maxX = Math.max(maxX, point.x);
          minY = Math.min(minY, point.y); maxY = Math.max(maxY, point.y);
        });
        const width = maxX - minX;
        const height = maxY - minY;
        const aspectRatio = Math.min(width, height) / Math.max(width, height);
        if (aspectRatio > 0.6 && width < canvas.width * 0.3 && height < canvas.height * 0.5) {
          return { valid: false, reason: "Simple shapes are not accepted. Please sign your full name." };
        }
      }
    }
    // Detect X-mark patterns: multiple short straight strokes (like "X", "XX", "XXX")
    if (strokeCount >= 2) {
      let straightStrokes = 0;
      let totalStrokeLength = 0;
      signatureData.forEach(stroke => {
        if (stroke.length < 3) { straightStrokes++; return; }
        const first = stroke[0];
        const last = stroke[stroke.length - 1];
        const directDist = Math.sqrt(Math.pow(last.x - first.x, 2) + Math.pow(last.y - first.y, 2));
        let pathLength = 0;
        for (let i = 1; i < stroke.length; i++) {
          pathLength += Math.sqrt(Math.pow(stroke[i].x - stroke[i-1].x, 2) + Math.pow(stroke[i].y - stroke[i-1].y, 2));
        }
        totalStrokeLength += pathLength;
        const straightness = pathLength > 0 ? directDist / pathLength : 1;
        if (straightness > 0.85) straightStrokes++;
      });
      const straightRatio = straightStrokes / strokeCount;
      const avgPointsPerStroke = totalPoints / strokeCount;
      if (straightRatio > 0.85 && avgPointsPerStroke < 25) {
        return { valid: false, reason: "Simple marks like 'X' are not accepted. Please sign your full name." };
      }
      if (straightRatio > 0.8 && totalStrokeLength < canvas.width * 0.5) {
        return { valid: false, reason: "Signature too simple. Please sign your full name legibly." };
      }
    }
    // Detect drawings / "XX" marks / squarish doodles by checking the overall
    // bounding box of ALL strokes. Real signatures are written across the line
    // and are at least 2x wider than tall — even compact ones (e.g. "Watson")
    // hit 2.5:1 in practice. Drawings (e.g. a house with a tree) and "XX"
    // marks are roughly square (~1:1). This single check catches both cases
    // that the previous straight-line/ink-percent rules missed.
    if (signatureData.length > 0) {
      let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
      signatureData.forEach(stroke => {
        stroke.forEach(p => {
          if (p.x < minX) minX = p.x;
          if (p.x > maxX) maxX = p.x;
          if (p.y < minY) minY = p.y;
          if (p.y > maxY) maxY = p.y;
        });
      });
      const bboxWidth = maxX - minX;
      const bboxHeight = maxY - minY;
      // Guard against divide-by-zero on a perfectly horizontal stroke (1px tall).
      if (bboxHeight >= 3 && bboxWidth / bboxHeight < 1.5) {
        return {
          valid: false,
          reason: "Please sign your full name across the line (not initials, marks, or drawings).",
        };
      }
    }
    return { valid: true };
  };

  const handleCheckIn = async () => {
    stopAllAudio();
    
    if (employeeMessage && !messageAcknowledged) {
      setShowMessageModal(true);
      toast.error(language === 'es' ? 'Por favor lea y reconozca el mensaje importante primero' : 'Please read and acknowledge the important message first');
      return;
    }
    if (employeeStatus !== 'found') {
      toast.error("Please enter a valid employee number");
      if (employeeNumber.length >= 5) {
        logFailedCheckin({
          employeeNumber, employeeName, roomNumber,
          reason: "Employee number not verified",
          detail: "Lookup said not-found — could be a wrong number OR no internet during verification",
        });
      }
      return;
    }
    if (!roomNumber) {
      toast.error("Please enter room number");
      return;
    }
    // Only validate the room number locally if the room list actually loaded.
    // If it never loaded (page opened while offline), fail open and let the
    // server decide — otherwise every room would be rejected as "not valid".
    if (roomsLoaded) {
      const validRoom = availableRooms.find(r => r.room_number === roomNumber.trim());
      if (!validRoom) {
        toast.error(`Room ${roomNumber} is not valid. Please check the room number on your key.`);
        logFailedCheckin({
          employeeNumber, employeeName, roomNumber,
          reason: `Room ${roomNumber} not found in room list`,
          detail: "Guest may have typed the wrong room number",
        });
        return;
      }
    }
    if (!date || !time) {
      toast.error("Please select date and time");
      return;
    }
    const signatureValidation = validateSignature();
    if (!signatureValidation.valid) {
      toast.error(signatureValidation.reason);
      logFailedCheckin({
        employeeNumber, employeeName, roomNumber,
        reason: "Signature rejected",
        detail: signatureValidation.reason,
      });
      return;
    }

    setLoading(true);
    try {
      const signature = sigRef.current.toDataURL();
      const checkinData = {
        employee_number: employeeNumber,
        room_number: roomNumber,
        check_in_date: format(date, "yyyy-MM-dd"),
        check_in_time: time,
        signature,
        telegram_username: telegramUsername.trim().replace('@', '')
      };

      // Auto-retry on transient errors (network failure / 503 / 504).
      // This protects against brief MongoDB Atlas primary failovers where a
      // first attempt may fail but a retry 1–2 seconds later succeeds.
      const MAX_ATTEMPTS = 3;
      let lastErr = null;
      let checkinSucceeded = false;
      for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
        try {
          await axios.post(`${API}/checkin`, checkinData, { timeout: 20000 });
          checkinSucceeded = true;
          break;
        } catch (err) {
          lastErr = err;
          const status = err?.response?.status;
          const isTransient = !err.response // network error
            || status === 503 || status === 504 || status === 502
            || err.code === 'ECONNABORTED' || err.message === 'Network Error';
          if (!isTransient || attempt === MAX_ATTEMPTS) {
            throw err;
          }
          if (attempt === 1) {
            toast.info(language === 'es'
              ? 'Sincronizando con la base de datos, un momento...'
              : 'Syncing with database, one moment…');
          }
          await new Promise(r => setTimeout(r, 1500 * attempt));
        }
      }
      if (!checkinSucceeded) throw lastErr;

      if (acceptedNoticeId && employeeName) {
        try {
          await axios.put(`${API}/notice-acceptance/${acceptedNoticeId}`, {
            guest_name: employeeName,
            employee_number: employeeNumber
          });
          if (setAcceptedNoticeId) setAcceptedNoticeId(null);
        } catch (err) {
          console.log("Could not update notice acceptance with guest name:", err);
        }
      }
      
      setTimeout(() => playVoiceMessage("checkin_complete"), 500);
      
      const greeting = getTimeBasedGreeting();
      setSuccessMessage({
        title: `${greeting}!`,
        message: t.welcomeToHodlerInn,
        subMessage: t.haveGoodRest,
        type: "checkin"
      });
      setView("checkin-success");
      
      if (setNoticeAccepted) setNoticeAccepted(false);
      
      setTimeout(() => {
        stopAllAudio();
        setView("menu");
      }, 8000);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Check-in failed");
      logFailedCheckin({
        employeeNumber, employeeName, roomNumber,
        reason: error.response ? "Server rejected check-in" : "Network error — tablet had no connection",
        detail: error.response?.data?.detail || error.message || "",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
      className="w-full max-w-md pb-64"
    >
      <Card className="glass-card p-4 sm:p-6" data-testid="checkin-form-card">
        <CardHeader className="pb-3 sm:pb-4">
          <button 
            onClick={() => setView("menu")} 
            className="text-vault-text-secondary hover:text-vault-gold transition-colors mb-2 flex items-center gap-2"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-4 h-4" />
            {t.back}
          </button>
          <CardTitle className="font-outfit text-xl sm:text-2xl font-bold text-vault-text tracking-tight flex items-center gap-3">
            <LogIn className="w-5 h-5 sm:w-6 sm:h-6 text-vault-gold" />
            {t.guestCheckIn}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 sm:space-y-4">
          {/* Employee Number */}
          <div>
            <label className="vault-label text-xs sm:text-sm">{t.employeeNumber}</label>
            <div className="relative">
              <Hash className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-vault-gold" />
              <Input
                value={employeeNumber}
                onChange={(e) => {
                  const numericValue = e.target.value.replace(/[^0-9]/g, '');
                  setEmployeeNumber(numericValue);
                }}
                onFocus={(e) => setTimeout(() => e.target.scrollIntoView({ behavior: 'smooth', block: 'nearest' }), 300)}
                placeholder={t.tapToEnterNumber}
                className="vault-input pl-10 input-highlight text-base sm:text-lg h-11 sm:h-12"
                data-testid="checkin-employee-input"
                autoFocus
                inputMode="numeric"
                pattern="[0-9]*"
                type="tel"
              />
              {verifying && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  <div className="w-5 h-5 border-2 border-vault-gold border-t-transparent rounded-full animate-spin"></div>
                </div>
              )}
            </div>
          </div>

          {/* Employee Name */}
          <div>
            <label className="vault-label text-xs sm:text-sm">{t.name}</label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-vault-gold" />
              <Input
                value={employeeName}
                readOnly
                placeholder={t.nameAutoAppear}
                className={cn(
                  "vault-input pl-10 text-base sm:text-lg h-11 sm:h-12",
                  employeeStatus === 'found' && "border-emerald-500 bg-emerald-900/20",
                  employeeStatus === 'new_guest' && "border-amber-500 bg-amber-900/20",
                  employeeStatus === 'not_found' && "border-red-500 bg-red-900/20"
                )}
                data-testid="checkin-name-input"
              />
            </div>
            {employeeStatus === 'found' && (
              <p className="text-emerald-400 text-sm mt-1 flex items-center gap-1">
                <span>✓</span> {t.welcome}
              </p>
            )}
            
            {/* Employee Reminder Banner */}
            {employeeStatus === 'found' && employeeMessage && !messageAcknowledged && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-3 p-3 bg-amber-500/20 border-2 border-amber-500 rounded-lg cursor-pointer"
                onClick={() => setShowMessageModal(true)}
              >
                <div className="flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="text-amber-500 font-bold text-sm">
                      {language === 'es' ? '⚠️ MENSAJE IMPORTANTE' : '⚠️ IMPORTANT MESSAGE'}
                    </p>
                    <p className="text-vault-text text-sm mt-1 line-clamp-2">{employeeMessage.message}</p>
                    <p className="text-amber-400 text-xs mt-1 underline">
                      {language === 'es' ? 'Toca para leer y confirmar' : 'Tap to read and acknowledge'}
                    </p>
                  </div>
                </div>
              </motion.div>
            )}
            
            {/* Employee Not Found */}
            {employeeStatus === 'not_found' && (
              <motion.div 
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
                className="mt-2 bg-amber-900/30 border border-amber-600/50 rounded-lg p-4 space-y-3"
              >
                <div className="text-center">
                  <p className="text-amber-400 text-lg font-bold mb-2">⚠️ {t.employeeIdNotFound}</p>
                  <p className="text-amber-300 text-sm">{t.notInSystem}</p>
                </div>
                
                {similarNumbers.length > 0 && (
                  <div className="bg-blue-900/40 border border-blue-500/50 rounded-lg p-3">
                    <p className="text-blue-400 text-sm font-medium mb-2">{t.didYouMean}</p>
                    <div className="space-y-2">
                      {similarNumbers.map((emp, idx) => (
                        <button
                          key={idx}
                          onClick={() => {
                            setEmployeeNumber(emp.employee_number);
                            setEmployeeStatus(null);
                            setSimilarNumbers([]);
                          }}
                          className="w-full text-left p-2 bg-blue-800/30 hover:bg-blue-700/40 rounded border border-blue-500/30 transition-colors"
                          data-testid={`similar-number-${idx}`}
                        >
                          <span className="text-blue-300 font-mono font-bold">{emp.employee_number}</span>
                          {emp.name && <span className="text-blue-400 text-sm ml-2">- {emp.name}</span>}
                        </button>
                      ))}
                    </div>
                    <p className="text-blue-300 text-xs mt-2">{t.tapCorrectNumber}</p>
                  </div>
                )}
                
                <div className="bg-yellow-900/50 border-2 border-yellow-500 rounded-lg p-4">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="bg-yellow-500 w-8 h-8 rounded flex items-center justify-center">
                      <span className="text-black text-lg">📝</span>
                    </div>
                    <p className="text-yellow-400 font-bold text-base">{t.pleaseUseYellowCard}</p>
                  </div>
                  <p className="text-yellow-300 text-sm">{t.yellowCardInstructions}</p>
                  <p className="text-yellow-300 text-sm mt-1">{t.adminWillAdd}</p>
                </div>
                
                <p className="text-vault-text-secondary text-xs text-center">{t.ifError}</p>
              </motion.div>
            )}
          </div>

          {/* Rest of form when employee found */}
          {employeeStatus === 'found' && (
            <>
              <div>
                <label className="vault-label text-xs sm:text-sm">{t.roomNumber}</label>
                <div className="relative">
                  <DoorOpen className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-vault-gold" />
                  <Input
                    ref={roomInputRef}
                    value={roomNumber}
                    onChange={(e) => setRoomNumber(e.target.value)}
                    onFocus={(e) => setTimeout(() => e.target.scrollIntoView({ behavior: 'smooth', block: 'nearest' }), 300)}
                    placeholder={t.tapToEnterRoom}
                    className="vault-input pl-10 input-highlight text-base sm:text-lg h-11 sm:h-12"
                    data-testid="checkin-room-input"
                  />
                </div>
              </div>

              <div className="space-y-3">
                <div>
                  <label className="vault-label text-xs sm:text-sm">{t.date}</label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "w-full vault-input justify-start text-left font-normal h-11 sm:h-12 text-sm sm:text-base",
                          !date && "text-muted-foreground"
                        )}
                        data-testid="checkin-date-btn"
                      >
                        <CalendarIcon className="mr-1 sm:mr-2 h-4 w-4 text-vault-text-secondary" />
                        {date ? format(date, "dd MMM") : t.date}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0 bg-vault-surface border-vault-border" align="start">
                      <Calendar mode="single" selected={date} onSelect={setDate} initialFocus className="bg-vault-surface text-vault-text" />
                    </PopoverContent>
                  </Popover>
                </div>

                <div>
                  <label className="vault-label text-xs sm:text-sm">{t.time}</label>
                  <TimeSelector value={time} onChange={setTime} dataTestId="checkin-time-selector" />
                </div>
              </div>

              {/* Telegram */}
              <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-3">
                <label className="vault-label text-xs sm:text-sm flex items-center gap-2">
                  <Send className="w-4 h-4 text-blue-400" />
                  {language === 'es' ? '📱 Telegram para alertas (opcional)' : '📱 Telegram for alerts (optional)'}
                </label>
                <div className="flex gap-2 mt-2">
                  <Input
                    value={telegramUsername}
                    onChange={(e) => setTelegramUsername(e.target.value)}
                    placeholder={language === 'es' ? '@tu_usuario_telegram' : '@your_telegram_username'}
                    className="vault-input text-sm h-10 flex-1"
                    data-testid="checkin-telegram-input"
                  />
                  {telegramUsername && hadTelegramSaved && (
                    <button
                      type="button"
                      onClick={handleTelegramOptOut}
                      className="px-3 h-10 text-xs bg-red-900/30 hover:bg-red-900/50 text-red-400 border border-red-500/30 rounded-md transition-colors"
                      data-testid="telegram-opt-out-btn"
                    >
                      {language === 'es' ? 'Cancelar' : 'Opt Out'}
                    </button>
                  )}
                </div>
                <p className="text-blue-400/70 text-xs mt-2">
                  {telegramUsername ? (
                    language === 'es' ? '✅ Ya estás registrado para recibir alertas de emergencia' : '✅ You\'re already signed up for emergency alerts'
                  ) : (
                    language === 'es' ? '💡 Te notificaremos si hay interrupciones (agua, electricidad, etc.)' : '💡 We\'ll notify you of any outages (water, power, etc.)'
                  )}
                </p>
              </div>

              {/* Signature */}
              <div ref={signatureContainerRef}>
                <div className="flex justify-between items-center mb-2">
                  <label className="vault-label text-xs sm:text-sm mb-0">{t.signature}</label>
                  <button 
                    onClick={clearSignature}
                    className="text-vault-text-secondary hover:text-vault-gold text-xs flex items-center gap-1"
                    data-testid="clear-checkin-signature-btn"
                  >
                    <Eraser className="w-3 h-3" /> {t.clear}
                  </button>
                </div>
                <div 
                  className="relative bg-white border-2 border-amber-500 rounded-lg touch-none select-none"
                  style={{ touchAction: 'none', WebkitUserSelect: 'none', userSelect: 'none' }}
                  onTouchStart={(e) => {
                    // Stop the touch from bubbling to scrollable parents but DON'T
                    // call lockBodyScroll here — SignatureCanvas's onBegin handles
                    // it. Locking from two places caused thrash on iOS.
                    e.stopPropagation();
                    if (document.activeElement && document.activeElement.tagName === 'INPUT') {
                      document.activeElement.blur();
                    }
                  }}
                  onTouchMove={(e) => e.stopPropagation()}
                >
                  <SignatureCanvas
                    ref={sigRef}
                    onBegin={() => {
                      if (document.activeElement && document.activeElement.tagName === 'INPUT') {
                        document.activeElement.blur();
                      }
                      speakSignatureReminder();
                      lockBodyScroll();
                    }}
                    onEnd={unlockBodyScroll}
                    canvasProps={{
                      className: "signature-canvas w-full",
                      style: { width: "100%", height: "150px", touchAction: "none", backgroundColor: "white", userSelect: "none", WebkitUserSelect: "none", WebkitTouchCallout: "none" }
                    }}
                    penColor="#000000"
                    backgroundColor="white"
                    minWidth={3}
                    maxWidth={6}
                  />
                  {/* Sign-here guide — an "X" + dashed line + "Sign here" label,
                      rendered as a DOM overlay POSITIONED IN FRONT OF the canvas
                      but with `pointer-events: none` so the employee's tap goes
                      straight through to the canvas. The guide is never painted
                      on the canvas itself, so it doesn't appear in the exported
                      signature PNG — it's purely a visual hint.
                      Employees sign ABOVE the line; the thin dashed line in the
                      bottom 20% of the pad won't visually interfere with the ink. */}
                  <div
                    className="absolute inset-0 flex flex-col justify-end items-stretch px-4 pb-3 pointer-events-none"
                    aria-hidden="true"
                    data-testid="signature-guide"
                  >
                    <div className="flex items-end gap-2">
                      <span className="text-gray-400 font-bold text-xl leading-none select-none" style={{ marginBottom: '-2px' }}>✕</span>
                      <div className="flex-1 border-b-2 border-dashed border-gray-300" style={{ height: '1px' }} />
                    </div>
                    <span className="text-gray-400 text-[10px] mt-1 ml-7 select-none">Sign here</span>
                  </div>
                </div>
              </div>

              <Button
                onClick={handleCheckIn}
                disabled={loading}
                className="w-full vault-btn-primary h-12 sm:h-14 text-base sm:text-lg font-bold"
                data-testid="submit-checkin-btn"
              >
                {loading ? t.processing : t.completeCheckIn}
              </Button>
            </>
          )}
        </CardContent>
      </Card>

      {/* Employee Reminder Message Modal */}
      {showMessageModal && employeeMessage && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-vault-surface border-2 border-amber-500 rounded-xl p-6 max-w-md w-full shadow-2xl"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-full bg-amber-500/20 flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-amber-500" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-amber-500">
                  {language === 'es' ? 'Mensaje Importante' : 'Important Message'}
                </h3>
                <p className="text-vault-text-secondary text-sm">
                  {language === 'es' ? 'Por favor lea antes de continuar' : 'Please read before continuing'}
                </p>
              </div>
            </div>
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4 mb-6">
              <p className="text-vault-text text-lg leading-relaxed">{employeeMessage.message}</p>
            </div>
            <Button
              onClick={handleAcknowledgeMessage}
              className="w-full bg-amber-500 hover:bg-amber-600 text-black font-bold h-14 text-lg"
              data-testid="acknowledge-message-btn"
            >
              {language === 'es' ? 'ENTIENDO' : 'I UNDERSTAND'}
            </Button>
          </motion.div>
        </div>
      )}
    </motion.div>
  );
}
